import React from 'react'
import { Card, CardContent } from './Card'
import { cn } from '@/lib/utils'

export type StatItem = {
  label: string
  value: React.ReactNode
  icon?: React.ReactNode
  tone?: 'primary' | 'success' | 'warning' | 'violet'
  onClick?: () => void
  active?: boolean
}

export function StatCards({ items, className }: { items: StatItem[]; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {items.map((it, idx) => (
        <Card
          key={idx}
          className={cn(it.onClick && 'cursor-pointer transition-colors hover:border-primary/40', it.active && 'border-primary/60')}
          role={it.onClick ? 'button' : undefined}
          tabIndex={it.onClick ? 0 : undefined}
          onClick={it.onClick}
          onKeyDown={(e) => { if (it.onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); it.onClick() } }}
          aria-pressed={it.active}
        >
          <CardContent className="flex items-center justify-between gap-2 p-3 md:p-4">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-[11px] text-foreground/70 leading-tight break-words">
                {it.label}
              </div>
              <div className={cn('text-lg sm:text-xl font-semibold', toneClass(it.tone))}>
                {it.value}
              </div>
            </div>
            {it.icon && (
              <div className={cn('rounded-lg p-1.5 sm:p-2', bgClass(it.tone))}>{it.icon}</div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function toneClass(t?: StatItem['tone']): string {
  switch (t) {
    case 'success': return 'text-emerald-400'
    case 'warning': return 'text-amber-400'
    case 'violet': return 'text-fuchsia-400'
    case 'primary':
    default: return 'text-primary'
  }
}

function bgClass(t?: StatItem['tone']): string {
  switch (t) {
    case 'success': return 'bg-emerald-500/10 text-emerald-400'
    case 'warning': return 'bg-amber-500/10 text-amber-400'
    case 'violet': return 'bg-fuchsia-500/10 text-fuchsia-400'
    case 'primary':
    default: return 'bg-primary/10 text-primary'
  }
}
